import React from 'react'
import { cn } from '../../../lib/utils'
import {
  ExerciseIcon,
  BuilderIcon,
  ShortCourseIcon,
  SampleIcon,
  ArticleIcon,
  SearchIcon,
  CollectionIcon,
  ScriptsIcon,
  QuizIcon,
  AudioIcon,
  WebinarIcon,
} from './icons'
import parse from 'html-react-parser'
import { BrainResearch, MagicWand } from 'iconoir-react'
import { DocumentTextIcon } from '@heroicons/react/24/outline'
export const iconMap = {
  builder: <BuilderIcon />,
  sample: <SampleIcon />,
  workshop: <ShortCourseIcon />,
  short_course: <ShortCourseIcon />,
  article: <ArticleIcon />,
  exercise: <ExerciseIcon />,
  search: <SearchIcon />,
  collection: <CollectionIcon />,
  script: <ScriptsIcon />,
  quiz: <QuizIcon />,
  assessment: <BrainResearch />,
  webinar: <WebinarIcon />,
  video: <WebinarIcon />,
  audio: <AudioIcon />,
  sample_docs: <DocumentTextIcon />,
  ayla: <MagicWand />,
}
import { Button } from '../../../lib/ui/button'
import NewCoverLetterModal from '../../CoverLetterBuilder/NewCoverLetterModal'
import { useIsDesktop, useIsMobile } from '../../../hooks/useMediaQuery'
import { useTranslation } from '../../../hooks/useTranslation'
import HeadshotCircle from '~/components/coach_services/MeetYourCoach/HeadshotCircle'

const CtaContent = ({
  path,
  ctaClasses,
  ctaOptions,
  cta,
  cta2,
  mobileCta,
  completed,
  retakePath,
  variant,
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const retakeCta = t('retake_exercise').includes('missing "')
    ? `${t('retake')} ${cta.split(' ').slice(-1)[0]}`
    : t('retake_exercise')

  const safePath = path && path.startsWith('javascript:') ? '#' : path
  const handleNoOpClick =
    path && path.startsWith('javascript:')
      ? (e) => e.preventDefault()
      : undefined

  if (path.match('cover_letters/new_ai_or_builder')) {
    return <NewCoverLetterModal />
  } else if (isMobile && mobileCta) {
    return (
      <a
        href={safePath}
        onClick={handleNoOpClick}
        className={ctaClasses}
        target={ctaOptions && ctaOptions['target']}
        rel={ctaOptions && ctaOptions['rel']}
        title={ctaOptions && ctaOptions['title']}
      >
        <Button variant={completed ? 'completed' : variant}>
          {parse(mobileCta)}
        </Button>
      </a>
    )
  } else {
    return (
      <>
        <a
          href={safePath}
          onClick={handleNoOpClick}
          className={ctaClasses}
          target={ctaOptions && ctaOptions['target']}
          rel={ctaOptions && ctaOptions['rel']}
          title={ctaOptions && ctaOptions['title']}
        >
          <Button variant={completed ? 'completed' : variant}>
            {completed ? t('view_results') : cta}
          </Button>
        </a>
        {cta2?.length > 0 ? parse(cta2) : ''}
        {completed && (
          <a
            href={retakePath || safePath + '?retake=true'}
            title={retakeCta}
            className="hover:!underline focus:!underline active:!underline"
          >
            {retakeCta}
          </a>
        )}
      </>
    )
  }
}

const ExerciseCard = ({ exercise, className }) => {
  const isDesktop = useIsDesktop()
  const {
    headerImageUrl,
    path,
    title,
    description,
    cta,
    cta2,
    mobile_cta,
    type,
    ctaClasses,
    ctaOptions,
    linkCta,
    completed,
    retakePath,
    coachServices,
    meetsMinimumProgramLevel,
    secondaryCta,
    secondaryPath,
    videoEmbedUrl,
  } = exercise

  const isVideo = type === 'video' && Boolean(videoEmbedUrl)

  const icon = Object.prototype.hasOwnProperty.call(iconMap, type) ? (
    iconMap[type]
  ) : (
    <ExerciseIcon />
  )

  const classes = coachServices
    ? 'bg-willow-brook text-intoo-green-coachcta min-h-[16rem]'
    : 'bg-lily-blue text-primary'

  const iconClasses = coachServices
    ? 'stroke-intoo-green-coachcta'
    : 'stroke-primary'

  return (
    (meetsMinimumProgramLevel || meetsMinimumProgramLevel === undefined) && (
      <div
        className={cn(
          'flex flex-col justify-between rounded-2.5xl md:flex-row',
          classes,
          className,
          'box-border w-full',
        )}
      >
        <div
          className={cn(
            'w-full self-center px-4 py-6 md:col-span-2 md:p-6',
            'max-w-full',
            isVideo && 'md:min-w-0 md:flex-1',
          )}
        >
          <div className="mb-4 flex flex-wrap items-center gap-4">
            {React.cloneElement(icon, {
              className: cn('mb-2 h-10 w-10 stroke-2', iconClasses),
            })}
            <h2
              role="heading"
              tabIndex="0"
              aria-level="2"
              className="!my-2 break-words font-roboto-slab text-xl font-bold focus:overflow-visible"
              title={title}
            >
              {title}
            </h2>
          </div>

          <p className="!mb-4 break-words font-roboto-slab">{description}</p>
          {linkCta && (
            <div className="!mb-4 flex w-full flex-row items-center overflow-x-auto">
              {parse(linkCta)}
            </div>
          )}
          <div
            className={cn(
              'flex w-full flex-col items-stretch gap-4 self-center md:flex-row md:items-center',
              'max-w-full',
            )}
          >
            {path && (
              <CtaContent
                path={path}
                ctaClasses={ctaClasses}
                ctaOptions={ctaOptions}
                cta={cta}
                cta2={cta2}
                completed={completed}
                retakePath={retakePath}
                variant={coachServices ? 'coachcta' : 'default'}
                mobileCta={mobile_cta}
              />
            )}
            {secondaryCta && (
              <CtaContent
                path={secondaryPath}
                cta={secondaryCta}
                completed={completed}
                retakePath={retakePath}
                variant={'default'}
              />
            )}
          </div>
        </div>
        {(isDesktop || isVideo) && (
          <>
            {coachServices && (
              <div className="py-6 pr-6">
                <HeadshotCircle coachImage={headerImageUrl} />
              </div>
            )}
            {!coachServices && isVideo && (
              <VideoEmbed videoEmbedUrl={videoEmbedUrl} title={title} />
            )}
            {!coachServices && !isVideo && (
              <CardImage headerImageUrl={headerImageUrl} />
            )}
          </>
        )}
      </div>
    )
  )
}

export default ExerciseCard

const CardImage = ({ headerImageUrl, className }) => {
  return (
    <div
      className={cn(
        `overflow-none tw-hidden visible rounded-r-2.5xl bg-cover bg-center bg-no-repeat sm:block md:min-w-[255px]`,
        className,
      )}
      style={{ backgroundImage: `url(${headerImageUrl})` }}
    ></div>
  )
}

const VideoEmbed = ({ videoEmbedUrl, title }) => {
  return (
    <div className="flex w-full items-center justify-center overflow-hidden rounded-b-2.5xl md:w-[38%] md:min-w-[280px] md:max-w-[520px] md:shrink-0 md:rounded-b-none md:rounded-r-2.5xl lg:min-w-[360px]">
      <div className="aspect-[16/9] w-full">
        <iframe
          src={videoEmbedUrl}
          title={title}
          className="h-full w-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          scrolling="no"
          frameBorder="0"
        />
      </div>
    </div>
  )
}
